<template>
  <div class="fullSize">
    <v-row justify="center" no-gutters class="pb-3">
      <v-col
        class="pb-0 pt-0 px-3"
        style="max-height: 95px; margin-bottom: -1px"
        cols="12"
        sm="12"
      >
        <v-text-field
          v-model="searchSymbol"
          :value="searchSymbol"
          outlined
          :placeholder="regexSearch ? 'Search with regex' : 'Search'"
          single-line
          dense
          :rules="regexSearch ? [rules.validRegex] : []"
          :class="regexSearch ? 'text-field-top-details' : 'hide-error-message'"
          :hide-details="!regexSearch"
          class="inputSlot-mb-0"
          style="z-index: 0"
        >
          <template v-slot:append>
            <v-row class="mx-0">
              <p class="mb-0 mx-2 pt-1 grey--text subtitle-2">
                ({{ currentSymbols }})
              </p>
              <v-btn
                @click="regexSearch = !regexSearch"
                icon
                small
                class="mb-2"
                :color="regexSearch ? 'primary' : 'white'"
              >
                <div class="absolute" style="left: 12px; font-size: 17px">
                  *
                </div>
                <div
                  class="absolute"
                  style="left: 5px; font-size: 25px; margin-bottom: 2px"
                >
                  .
                </div>
              </v-btn>
            </v-row>
          </template>
        </v-text-field>
      </v-col>
      <v-col class="pt-0 px-3" cols="12">
        <v-data-table
          id="selectSymbolsTable"
          ref="PriceWatcherSymbolsTable"
          @current-items="(items) => setCurrentSymbols(items)"
          dense
          hide-default-header
          :search="searchSymbol"
          :custom-filter="symbolSearchFilter"
          :items-per-page="10"
          :items="allPricewatcherSymbols"
          :headers="[{ text: '', value: 'value' }, { text: '', value: 'alias', align: ' d-none' }]"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100, -1],
          }"
          :class="{ 'cc-data-table-empty': !allPricewatcherSymbols.length }"
          class="data-table-inside-footer"
          style="
            height: 238px;
            overflow-y: auto;
            border: 1px solid #47bd96;
            z-index: 1;
          "
        >
          <template v-slot:no-data>
            <v-row justify="center">
              <v-col v-if="loadingPWSymbols" cols="2">
                <v-row justify="end">
                  <v-progress-circular
                    style="margin-top: -4px"
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </v-row>
              </v-col>
              <v-col :cols="loadingPWSymbols ? 5 : 10">
                <p
                  :style="
                    loadingPWSymbols
                      ? 'text-align: left;'
                      : 'text-align: center;'
                  "
                >
                  {{ tableMessage }}
                </p>
                <v-btn
                  v-if="!loadingPWSymbols"
                  rounded
                  outlined
                  color="primary"
                  style="font-weight: 400"
                  @click="
                    $router.push({
                      path: '/applications',
                      query: { add: true },
                    })
                  "
                  >Run Applications</v-btn
                >
              </v-col>
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr class="accent">
              <td class="primary--text py-1">
                <v-row class="mx-0">
                  <div @click="symbolSelected(item)">
                    <v-row class="mx-0">
                      <v-icon color="primary" class="mt-2">
                        {{
                          item.checked
                            ? "radio_button_checked"
                            : "radio_button_unchecked"
                        }}
                      </v-icon>
                      <div class="ml-1" style="width: 108px; margin-top: 10px">
                        {{ item.value }}
                      </div>
                    </v-row>
                  </div>

                  <v-text-field
                    :disabled="!item.checked"
                    v-model="item.alias"
                    @click.stop
                    label="Alias"
                    dense
                    hide-details
                    :class="{ 'ml-6': $vuetify.breakpoint.width > 500 }"
                    class="mini-text-field"
                    style="max-width: 100px; height: 28px; margin-bottom: 6px"
                  ></v-text-field>
                  <v-text-field
                    :disabled="!item.checked"
                    v-model.number="item.multiplier"
                    @click.stop
                    type="number"
                    label="Spread Multiplier"
                    dense
                    hide-details
                    :class="$vuetify.breakpoint.width > 500 ? 'ml-8' : 'ml-1'"
                    class="mini-text-field"
                    style="max-width: 100px; height: 28px; margin-bottom: 6px"
                  ></v-text-field>
                </v-row>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row justify="start" class="mx-0 pl-3">
      <v-btn
        :disabled="!allPricewatcherSymbols.length"
        color="primary"
        style="font-weight: 400"
        text
        @click="selectAll"
        >Add All
      </v-btn>
      <v-btn
        :disabled="!allPricewatcherSymbols.length"
        color="primary"
        style="font-weight: 400"
        text
        @click="deSelectAll"
        >Remove All
      </v-btn>
      <p class="mb-0 mx-4 pt-2 grey--text subtitle-2">
        Selected: {{ currentSelectedSymbols }}/{{ currentSymbols
        }}{{
          allPricewatcherSymbols.length > currentSymbols
            ? `&nbsp;&nbsp;&nbsp;Total selected: ${checkedSymbolsCount}/${allPricewatcherSymbols.length}`
            : ""
        }}
      </p>
    </v-row>
  </div>
</template>

<script>
import { PW_SERVER_STATUSES, LOADING_STATES } from "../../utilities/enums.js";

export default {
  name: "SymbolSelector",
  data() {
    return {
      PW_SERVER_STATUSES: PW_SERVER_STATUSES,
      currentSymbols: 0,
      currentSelectedSymbols: 0,
      searchSymbol: null,
      regexSearch: false,
      rules: {
        validRegex: (regex) => {
          return (
            this.isValidRegex(regex, false) || "Invalid regular expression"
          );
        },
      },
    };
  },
  computed: {
    tableMessage() {
      if (this.loadingPWSymbols) return "Loading Instruments...";

      if (this.PWSymbolsState == LOADING_STATES.ERROR) {
        switch (this.PWServerStatus) {
          case PW_SERVER_STATUSES.ERROR_404:
            if (this.appCode == "PW") return "This watcher is not created yet";
            else return "Application not created yet";
            break;
          case PW_SERVER_STATUSES.ERROR:
            return "An error has occurred..";
            break;
        }
      }

      return "Something went wrong. Application might not be running.";
    },
    loadingPWSymbols() {
      return this.PWSymbolsState == LOADING_STATES.LOADING;
    },
    allPricewatcherSymbols: {
      get() {
        return this.value;
      },
      set(ev) {
        return this.$emit(input, ev);
      },
    },
    checkedSymbolsCount() {
      let i = 0;
      this.allPricewatcherSymbols.forEach((el) => {
        if (el.checked) i++;
      });
      return i;
    },
  },
  methods: {
    isValidRegex(regexStr, returnRegex = true) {
      var isValid = true;
      try {
        var regex = new RegExp(regexStr);
      } catch (e) {
        isValid = false;
      }

      if (returnRegex && regex != undefined) {
        return regex;
      } else return isValid;
    },
    selectAll() {
      // allPricewatcherSymbols
      let internalCurrentItems =
        this.$refs.PriceWatcherSymbolsTable.internalCurrentItems;
      internalCurrentItems.forEach((el) => {
        if (!this.allPricewatcherSymbols[el.index].checked)
          this.allPricewatcherSymbols[el.index].checked = true;
      });
      this.recalculateSelectedSymbolsLength();
    },
    deSelectAll() {
      let internalCurrentItems =
        this.$refs.PriceWatcherSymbolsTable.internalCurrentItems;
      internalCurrentItems.forEach((el) => {
        if (this.allPricewatcherSymbols[el.index].checked)
          this.allPricewatcherSymbols[el.index].checked = false;
      });
      this.recalculateSelectedSymbolsLength();
    },
    symbolSearchFilter(value, search, item) {
      if (this.regexSearch) {
        let regex = this.isValidRegex(search);
        if (regex) {
          return search != null && regex.test(value);
        } else return false;
      } else return search != null && value.indexOf(search) !== -1;
    },
    symbolSelected(item) {
      item.checked = !item.checked;
      item.checked
        ? this.currentSelectedSymbols++
        : this.currentSelectedSymbols--;
      this.recalculateSelectedSymbolsLength();
    },
    recalculateSelectedSymbolsLength() {
      let selected = 0;
      this.$refs.PriceWatcherSymbolsTable.internalCurrentItems.forEach((el) => {
        if (el.checked) selected++;
      });
      this.currentSelectedSymbols = selected;
    },
    setCurrentSymbols(items) {
      this.currentSymbols = items.length;
      this.currentSelectedSymbols = items.reduce((final, next) => {
        if (next.checked) final++;
        return final;
      }, 0);
    },
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    appCode: {
      type: String,
      default: "",
    },
    PWServerStatus: {
      type: Number,
      default: 0,
    },
    PWSymbolsState: {
      type: Number,
      default: 0,
    },
    // agentConfiguration: {
    //   type: Object,
    //   default: () => {},
    // },
    // allPricewatcherSymbols: {
    //   type: Array,
    //   default: () => [],
    // },
  },
};
</script>

<style>
.theme--dark .data-table-inside-footer .v-data-footer {
  background-color: #262b2e;
}
.theme--light .data-table-inside-footer .v-data-footer {
  background-color: #fff;
}
.data-table-inside-footer .v-data-footer {
  position: sticky;
  width: auto;
  border-color: #47bd96 !important;
}

.data-table-inside-footer .v-data-table__wrapper {
  overflow-y: hidden !important;
}

.cc-data-table-empty .v-data-table__wrapper {
  height: 100% !important;
}
</style>
